// A side view: one thing of another part (a Case, a person, a document)
// embedded beside the screen that opened it, while that screen stays as it is.
// The screen that hosts it (the Agent chat) decides where it sits and puts
// what is open in its own path; the part that owns the kind draws it through
// `defineScreenPart({ sideViews })`. Neither imports the other.
//
// Promise:
// - Inside a side view, `DetailHeader` and `PanelHeader` drop the sidebar and
//   back-to-list buttons and end with the side view's controls: open on its
//   own screen (the kind's favorite path) and close. Parts draw their usual
//   detail and get the frame for free.
// - A plain click on a reference or a change, where a host offers a side
//   view (`SideViewOpenerContext`), opens it there; a click with a modifier
//   key, a middle click, or a kind with no side view navigates as a link does.
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  createContext,
  type MouseEvent,
  type ReactNode,
  Suspense,
  useContext,
} from "react";
import { useMeApp } from "../app/context";
import { Skeleton } from "../ui/bits";
import { ICON_BUTTON } from "../ui/button";
import { Icon } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import { useShellFrame } from "./frame";
import { DetailHeader } from "./list-detail";

export interface SideViewFrame {
  kind: string;
  id: string;
  close(): void;
  /**
   * Where "open on its own screen" goes, when not the kind's favorite path
   * (a side chat: its own chat). null: no such link (not made yet).
   */
  full?: string | null;
  /**
   * The name of the screen it is the side of (a chat's title). Where the side
   * lies over that screen (too narrow to sit beside it), the header starts
   * with "← <name>", which closes the side (the old web's).
   */
  back?: string;
}

const SideViewContext = createContext<SideViewFrame | null>(null);

/** The side view this screen is drawn in, or null on its own screen. */
export const useSideView = () => useContext(SideViewContext);

/** Opens a thing in the side view of the screen around (true: it did). */
export interface SideViewOpener {
  open(kind: string, id: string): boolean;
  /** What the side view shows now. */
  current: { kind: string; id: string } | null;
}

export const SideViewOpenerContext = createContext<SideViewOpener | null>(null);

export const useSideViewOpener = () => useContext(SideViewOpenerContext);

/** A click that means "open it here" (not a new tab or window). */
export function isPlainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * The click handler of a link to a thing: opens it in the side view around
 * when that is a plain click and the kind has one; otherwise the link goes.
 */
export function useOpenInSide(): (
  e: MouseEvent,
  kind: string,
  id: string,
) => void {
  const opener = useSideViewOpener();
  return (e, kind, id) => {
    if (e.defaultPrevented || !opener || !isPlainClick(e)) return;
    if (opener.open(kind, id)) e.preventDefault();
  };
}

/** Whether a thing of this kind can be shown in a side view. */
export function useHasSideView(kind: string): boolean {
  return useMeApp().sideViews[kind] !== undefined;
}

/**
 * The registered view of a kind, in its frame. A kind without one (a path
 * written by hand) says it cannot be shown here, with the controls to leave.
 */
export function SideViewSlot({ kind, id, close, back }: SideViewFrame) {
  const View = useMeApp().sideViews[kind];
  return (
    <SideViewFrameProvider frame={{ kind, id, close, back }}>
      {View ? (
        // A part's view may be loaded on first use (app/lazy.tsx): until then
        // the frame shows the header's place and a skeleton, as views do
        // while their data loads.
        <Suspense fallback={<DetailLoading />}>
          <View key={`${kind}:${id}`} id={id} />
        </Suspense>
      ) : (
        <>
          <DetailHeader />
          <p className="px-5 pt-4 text-[13px] text-faint">
            {m.refs_cannot_open_here()}
          </p>
        </>
      )}
    </SideViewFrameProvider>
  );
}

/**
 * A detail beside a screen whose code is still loading (a side view, a
 * panel): its header's place and a skeleton, as details show while their
 * data loads.
 */
export function DetailLoading() {
  return (
    <>
      <DetailHeader title="" />
      <div className="px-5 pt-4">
        <Skeleton lines={[60, 40]} />
      </div>
    </>
  );
}

/**
 * Draws its children as the side of a screen (a side view, or a side chat):
 * their `DetailHeader` / `PanelHeader` drop the sidebar and back-to-list
 * buttons and end with the side's controls.
 */
export function SideViewFrameProvider({
  frame,
  children,
}: {
  frame: SideViewFrame;
  children: ReactNode;
}) {
  return (
    <SideViewContext.Provider value={frame}>
      {/* Links inside the side go as links do (the whole screen moves), not
          into the side again (the old web's). */}
      <SideViewOpenerContext.Provider value={null}>
        {children}
      </SideViewOpenerContext.Provider>
    </SideViewContext.Provider>
  );
}

/**
 * "← <the screen's name>" at the start of a side's header, only while the side
 * lies over that screen (the host is a container named `sidehost` under
 * 780 px: `RIGHT_PANE`). It closes the side. Nothing outside a side view or
 * without a name.
 */
export function SideViewBack() {
  const side = useSideView();
  if (!side?.back) return null;
  return (
    <button
      type="button"
      onClick={side.close}
      className="-ml-1 flex min-w-0 max-w-[40%] flex-none cursor-pointer items-center gap-1 rounded-[6px] px-1 py-0.5 text-[12px] text-muted hover:bg-hover hover:text-fg @min-[780px]/sidehost:hidden"
    >
      <Icon kind="back" size={13} />
      <span className="truncate">{side.back}</span>
    </button>
  );
}

/**
 * Open on its own screen and close; nothing outside a side view. One shape
 * for every side (a side view and a side chat): "open on its own screen" in
 * words (the old web's; an icon alone did not say where it goes), then ×.
 * In a narrow side the words took the room of the title (it read "[…"), so
 * below 560 px of header (the header is a container) the same link is an icon
 * with the words in a tooltip.
 */
export function SideViewControls() {
  const side = useSideView();
  const { favoriteKinds } = useMeApp();
  // On a phone the side covers the screen and closes from its Dock.
  const phone = useShellFrame().phone;
  if (!side) return null;
  const full =
    side.full !== undefined ? side.full : favoriteKinds[side.kind]?.to(side.id);
  return (
    <div className="flex flex-none items-center gap-1">
      {full && (
        <>
          <Link
            to={full}
            className="hidden flex-none whitespace-nowrap rounded-[6px] px-1.5 py-0.5 text-[12px] text-muted hover:bg-hover hover:text-fg @[560px]:inline"
          >
            {m.shell_side_open_full()}
          </Link>
          <Tooltip text={m.shell_side_open_full()}>
            <Link
              to={full}
              aria-label={m.shell_side_open_full()}
              className={`${ICON_BUTTON} @[560px]:hidden`}
            >
              <Icon kind="expand" size={13} />
            </Link>
          </Tooltip>
        </>
      )}
      {!phone && (
        <Tooltip text={m.shell_side_close()}>
          <button
            type="button"
            aria-label={m.shell_side_close()}
            onClick={side.close}
            className={ICON_BUTTON}
          >
            <Icon kind="close" size={13} />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
